import { AlertCircle, CheckCircle, XCircle } from "lucide-react";
import React from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";

interface SyncResult {
  errors: string[];
  message: string;
  newMeasurements: number;
  success: boolean;
  totalMeasurements: number;
  updatedMeasurements: number;
}

interface SyncResultCardProperties {
  syncResult: SyncResult;
}

/**
 * Sync Result Card Component
 * Displays the result of a synchronization operation
 *
 * @param root0
 *
 * @param root0.syncResult
 *
 * @returns {JSX.Element} Sync result card component
 *
 */
export const SyncResultCard: React.FC<SyncResultCardProperties> = ({ syncResult }) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700 text-white">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          {syncResult.success ? (
            <CheckCircle className="h-6 w-6 text-green-400" />
          ) : (
            <XCircle className="h-6 w-6 text-red-400" />
          )}
          <h3 className="text-xl font-semibold text-white">Last Synchronization</h3>
          <Badge className="ml-auto" variant={syncResult.success ? "success" : "destructive"}>
            {syncResult.success ? "Success" : "Failed"}
          </Badge>
        </div>

        <div
          className={`p-4 rounded-lg border ${
            syncResult.success
              ? "bg-green-500/10 border-green-500/30"
              : "bg-red-500/10 border-red-500/30"
          }`}
        >
          <p
            className={`font-semibold mb-4 ${
              syncResult.success ? "text-green-300" : "text-red-300"
            }`}
          >
            {syncResult.message}
          </p>

          {syncResult.success ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                <p className="text-slate-400 text-sm">New Measurements</p>
                <p className="text-lg font-semibold text-green-400">{syncResult.newMeasurements}</p>
              </div>
              <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                <p className="text-slate-400 text-sm">Updated Measurements</p>
                <p className="text-lg font-semibold text-blue-400">
                  {syncResult.updatedMeasurements}
                </p>
              </div>
              <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                <p className="text-slate-400 text-sm">Total Processed</p>
                <p className="text-lg font-semibold text-white">{syncResult.totalMeasurements}</p>
              </div>
            </div>
          ) : null}

          {syncResult.errors.length > 0 && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <p className="text-red-300 font-semibold">Errors:</p>
              </div>
              <ul className="list-disc list-inside text-red-300 text-sm space-y-1">
                {syncResult.errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
